/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Delete, Edit, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import uniqid from 'uniqid';
import { Cafe, Table, TableType } from '../../apiTypes';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { updateDoc } from '../../utils/api';
import useMedia from '../../utils/useMedia';
import AddTableButton from './AddTableButton';
import EditTable from './EditTable';
import UploadBG from './UploadBG';

export type TableSize = {
  [key in TableType]: {
    width: number;
    height: number;
  };
};

export const tableParams: { [key in TableType]: { w: number; h: number } } = {
  square: { w: 40, h: 40 },
  circle: { w: 35, h: 35 },
  rectangleH: { w: 25, h: 40 },
  rectangleV: { w: 40, h: 25 },
  barH: { w: 15, h: 60 },
  barV: { w: 60, h: 15 },
};

export const getTableSizes = (mapSize: number, scale: number): TableSize => ({
  square: {
    width: mapSize / ((scale * 40) / 100),
    height: mapSize / ((scale * 40) / 100),
  },
  circle: {
    width: mapSize / ((scale * 35) / 100),
    height: mapSize / ((scale * 35) / 100),
  },
  rectangleH: {
    width: mapSize / ((scale * 25) / 100),
    height: mapSize / ((scale * 40) / 100),
  },
  rectangleV: {
    width: mapSize / ((scale * 40) / 100),
    height: mapSize / ((scale * 25) / 100),
  },
  barH: {
    width: mapSize / ((scale * 15) / 100),
    height: mapSize / ((scale * 60) / 100),
  },
  barV: {
    width: mapSize / ((scale * 60) / 100),
    height: mapSize / ((scale * 15) / 100),
  },
});

const RestaurantTableMap = () => {
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const { tablesMap, cafeSlug } = useAdmin();
  const [_, setMounted] = useState(0);
  const [tables, setTables] = useState<Table[]>(tablesMap.tables);
  const [scale, setScale] = useState<number>(tablesMap.scale);
  const [bgUrl, setBgUrl] = useState<string>(tablesMap.bgUrl);
  const [isDirty, setIsDirty] = useState(false);
  const [editTable, setEditTable] = useState<Table | null>(null);
  const [draggingTable, setDraggingTable] = useState<string | null>(null);
  const [cursorOffset, setCursorOffset] = useState<{ x: number; y: number } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const tableSizes = getTableSizes(mapRef.current?.getBoundingClientRect().width || 500, scale);
  const mapRect = mapRef.current?.getBoundingClientRect() || { width: 500, height: 500, top: 0, left: 0 };

  const handleAddTable = (type: TableType, x: number, y: number) => {
    const name = String(Math.max(0, ...tables.map((t) => Number(t.name) || 0)) + 1);
    const newTable: Table = { id: uniqid(), name, type, x, y };
    setTables([...tables, newTable]);
    setIsDirty(true);
  };

  const handleDeleteTable = (id: string) => {
    const newTables = tables.filter((table) => table.id !== id);
    setTables(newTables);
    setIsDirty(true);
  };

  const handleTableMouseDown = (id: string, e: React.MouseEvent<HTMLDivElement>) => {
    setDraggingTable(id);
    const tableRect = e.currentTarget.getBoundingClientRect();
    const cursorOffsetX = e.clientX - tableRect.left;
    const cursorOffsetY = e.clientY - tableRect.top;
    setCursorOffset({ x: cursorOffsetX, y: cursorOffsetY });
  };

  const handleTableMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingTable !== null) {
      if (!mapRef.current) {
        return;
      }

      const newTables = tables.map((table) => {
        if (table.id === draggingTable) {
          const newTable = { ...table };
          const tableWidth = tableSizes[table.type].width;
          const tableHeight = tableSizes[table.type].height;
          const newTableX = (e.clientX - mapRect.left - cursorOffset!.x) / mapRect.width;
          const newTableY = (e.clientY - mapRect.top - cursorOffset!.y) / mapRect.height;

          const maxTableX = 1 - tableWidth / mapRect.width;
          const maxTableY = 1 - tableHeight / mapRect.height;

          if (newTableX < 0) {
            newTable.x = 0;
          } else if (newTableX > maxTableX) {
            newTable.x = maxTableX;
          } else {
            newTable.x = newTableX;
          }

          if (newTableY < 0) {
            newTable.y = 0;
          } else if (newTableY > maxTableY) {
            newTable.y = maxTableY;
          } else {
            newTable.y = newTableY;
          }
          return newTable;
        }
        return table;
      });
      setTables(newTables);
    }
  };

  const handleTableMouseUp = () => {
    setDraggingTable(null);
    setCursorOffset(null);
    setIsDirty(true);
  };

  const handleEditTable = (table: Table) => {
    const index = tables.findIndex((t) => t.id === table.id);
    setTables([...tables.slice(0, index), table, ...tables.slice(index + 1)]);
    setIsDirty(true);
  };

  const handleEditScale = (diff: number) => {
    setScale((prev) => prev + diff);
    setIsDirty(true);
  };

  const handleSaveMap = () => {
    updateDoc<Cafe>(`cafes/${cafeSlug}`, {
      'tablesMap.scale': scale,
      'tablesMap.tables': tables,
      'tablesMap.bgUrl': bgUrl,
    });
    setIsDirty(false);
  };

  useEffect(() => {
    setMounted(1);
    const id = setTimeout(() => setMounted(2), 500);
    return () => clearTimeout(id);
  }, []);

  return (
    <Box sx={{ maxWidth: 1000, m: '0 auto' }}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap={1}>
          <AddTableButton onAddTable={handleAddTable} />
          <UploadBG bgUrl={bgUrl} setBgUrl={setBgUrl} />
        </Box>
        <PrimaryButton variant="contained" disabled={!isDirty} onClick={handleSaveMap}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </Box>
      <Box
        ref={mapRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: mapRect.width * 0.7,
          border: (t) => `1px solid ${t.palette.grey[300]}`,
          backgroundColor: (t) => `${t.palette.background.paper}`,
          backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: 1,
          overflow: 'hidden',
          my: 2,
          mx: 'auto',
        }}
        onPointerMove={handleTableMouseMove}
        onPointerUp={handleTableMouseUp}
      >
        {tables.map((table) => (
          <Box
            key={table.id}
            id={`table-${table.id}`}
            onPointerDown={(e) => handleTableMouseDown(table.id, e)}
            sx={{
              position: 'absolute',
              top: `${table.y * 100}%`,
              left: `${table.x * 100}%`,
              backgroundColor: (t) => t.palette.secondary.main,
              border: (t) => `2px solid ${t.palette.secondary.dark}`,
              width: tableSizes[table.type].width,
              height: tableSizes[table.type].height,
              borderRadius: table.type === 'circle' ? '50%' : '3px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'move',
              touchAction: 'none',
              userSelect: 'none',
              '& button': {
                display: 'none',
              },
              ':hover': {
                '& button': {
                  display: 'block',
                },
              },
            }}
          >
            <Typography variant={isMobile ? 'body2' : 'body1'} color="white" textAlign="center">
              {table.name}
            </Typography>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteTable(table.id)}
              sx={{ position: 'absolute', top: -18, right: -18 }}
            >
              <Delete fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => setEditTable(table)}
              sx={{ position: 'absolute', top: -18, left: -18 }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Box display="flex" position="absolute" bottom={0} right={0}>
          <IconButton disabled={scale === 70} onClick={() => handleEditScale(10)}>
            <ZoomOut />
          </IconButton>
          <IconButton disabled={scale === 20} onClick={() => handleEditScale(-10)}>
            <ZoomIn />
          </IconButton>
        </Box>
      </Box>

      {editTable && <EditTable table={editTable} onClose={() => setEditTable(null)} onSubmit={handleEditTable} />}
    </Box>
  );
};

export default RestaurantTableMap;
